import { Injectable } from "@angular/core";
import { map, Observable } from "rxjs";
import * as _ from 'lodash';
import { IBillingDocument, IBillingDocumentFilter, IBillingDocumentNames, IBillingDocumentTypes } from "./billing-documents.interface";
import { HttpService } from "../../../../http.service";
import { AuthService } from "../../../shared/services/auth.service";
import { UtilsService } from "../../../shared/services/utils.service";
import { apiURL, EnvConfig } from "../../../../env-config";
import { ITask } from "../../tasks-page/tasks/task.interface";

let self: BillingDocumentService;

@Injectable()
export class BillingDocumentService {

  constructor(
    private _http: HttpService,
    private _utilsService: UtilsService,
    private _AuthService: AuthService
  ) {
    self = this;
  }

  getBillingDocuments(filter?: IBillingDocumentFilter): Observable<any> {
    let queryParams: string = !_.isEmpty(filter) ? '?' + this._utilsService.toQueryString(filter) : '';
    return this._http.get(`${apiURL(EnvConfig.API_BILLING_DOCUMENTS)}${queryParams}`);
  }


  getLastDocNum(): Observable<string> {
    let id = this._AuthService.companyId()
    const filter: IBillingDocumentFilter = {
      filters: {
        isDeleted: undefined
      },
      sort: '-number',
      skip: 0,
      limit: 20,
      company: id
    };

    return this.getBillingDocuments(filter).pipe(
      map((documents: any) => {
        if (documents && documents.length > 0) {
          return documents[0].docNumber || documents[0].number;
        } else {
          return '0';
        }
      })
    );
  }

  getBillingDocument(document: IBillingDocument | string): Observable<any> {
    let id = (typeof document == 'string') ? document : document._id;
    return this._http.get(`${apiURL(EnvConfig.API_BILLING_DOCUMENTS)}/${id}`);
  }

  getBillingDocumentByTask(task: ITask | string): Observable<any> {
    let taskId = (typeof task == 'string') ? task : task._id;
    return this.getBillingDocuments({
      filters: {},
      task: taskId,
      company: this._AuthService.companyId()
    }).pipe(map(documents => documents ? documents[0] : undefined));
  }

  createInvoice(document: IBillingDocument): Observable<any> {
    document.type = IBillingDocumentTypes.INVOICE as IBillingDocumentNames;
    return this.createBillingDocument(document);
  }

  createBillingDocument(document: IBillingDocument): Observable<any> {
    document.client = (typeof document.client == 'string') ? document.client : document.client?._id;
    return this._http.post(`${apiURL(EnvConfig.API_BILLING_DOCUMENTS)}`, document);
  }

  getBillingDocumentPDFURL(document: IBillingDocument | string, extended: boolean = false): string {
    let id = (typeof document == 'string') ? document : document._id;
    return `${apiURL(EnvConfig.API_BILLING_DOCUMENTS)}/${id}/generatePDF` + (extended ? '?extended=true' : '');
  }

  getBillingDocumentPDF(document: IBillingDocument | string, extended: boolean = true): Promise<any> {
    return new Promise((resolve, reject) => {
      let url = this.getBillingDocumentPDFURL(document, extended);
      this._http.get(url, { responseType: "arraybuffer" }).subscribe(file => {
        resolve(this._utilsService.blobToObjectURL(file, "application/pdf"));
      });
    });
  }

  download(id: string, name: string = 'document') {
    let query = { "company": this._AuthService.companyId(), "extended": true }
    let queryParams: string = '?' + this._utilsService.toQueryString(query);
    this._http.get(`${apiURL(EnvConfig.API_BILLING_DOCUMENTS)}/${id}/generatePdf${queryParams}`, { responseType: "arraybuffer" }).subscribe(file => {
      this._utilsService.blobToFile(file, "application/pdf", name + '.pdf');
    });
  }

  sendEmail(id?: string, data?: any): Observable<any> {
    return this._http.post(`${apiURL(EnvConfig.API_BILLING_DOCUMENTS)}/${id}/sendEmail`, data);
  }

  updateBillingDocument(document: IBillingDocument): Observable<any> {
    let id = document._id;
    delete document._id;
    return this._http.patch(`${apiURL(EnvConfig.API_BILLING_DOCUMENTS)}/${id}`, document);
  }

  issueInvoiceDocument(document: IBillingDocument): Observable<any> {
    let id = document._id;
    delete document._id;
    return this._http.patch(`${apiURL(EnvConfig.API_BILLING_DOCUMENTS)}/${id}/toIssued`, document);
  }

  deleteBillingDocument(document: IBillingDocument | string): Observable<void> {
    let id = (typeof document == 'string') ? document : document._id;
    return this._http.delete(`${apiURL(EnvConfig.API_BILLING_DOCUMENTS)}/${id}/delete`);
  }

  cancelBillingDocument(document: IBillingDocument | string): Observable<void> {
    let id = (typeof document == 'string') ? document : document._id;
    return this._http.post(`${apiURL(EnvConfig.API_BILLING_DOCUMENTS)}/${id}/cancel`, {});
  }

  restoreBillingDocument(document: IBillingDocument | string): Observable<void> {
    let id = (typeof document == 'string') ? document : document._id;
    return this._http.post(`${apiURL(EnvConfig.API_BILLING_DOCUMENTS)}/${id}/restore`, null);
  }

  createReceipt(data: any | string): Observable<void> {
    return this._http.post(`${apiURL(EnvConfig.API_RECEIPTS)}`, data);
  }

  getReceipt(document: any | string): Observable<any> {
    let id = (typeof document == 'string') ? document : document._id;
    return this._http.get(`${apiURL(EnvConfig.API_RECEIPTS)}/${id}`);
  }

  getReceiptPDFURL(document: any, extended: boolean = false): string {
    let id = (typeof document == 'string') ? document : document._id;
    return `${apiURL(EnvConfig.API_RECEIPTS)}/${id}/generatePDF`;
  }

  getReceiptPDF(document: any): Promise<any> {
    return new Promise((resolve, reject) => {
      let url = this.getReceiptPDFURL(document);
      this._http.get(url, { responseType: "arraybuffer" }).subscribe(file => {
        resolve(this._utilsService.blobToObjectURL(file, "application/pdf"));
      });
    });
  }

  downloadReceipt(id: string, name: string = 'receipt') {
    let query = { "company": this._AuthService.companyId() }
    let queryParams: string = '?' + this._utilsService.toQueryString(query);
    this._http.get(`${apiURL(EnvConfig.API_RECEIPTS)}/${id}/generatePdf${queryParams}`, { responseType: "arraybuffer" }).subscribe(file => {
      this._utilsService.blobToFile(file, "application/pdf", name + '.pdf');
    });
  }

  deleteReceipt(receipt: any): Observable<void> {
    let id = (typeof receipt == 'string') ? receipt : receipt._id;
    return this._http.delete(`${apiURL(EnvConfig.API_RECEIPTS)}/${id}`);
  }

  generateZipFile(filter?: IBillingDocumentFilter): Observable<any> {
    let queryParams: string = !_.isEmpty(filter) ? '?' + this._utilsService.toQueryString(filter) : '';
    return this._http.get(`${apiURL(EnvConfig.API_BILLING_DOCUMENTS)}/generateBulkZip${queryParams}`);
  }

  downloadExcelFile(query: any) {
    query.company = this._AuthService.companyId();
    let queryParams: string = '?' + this._utilsService.toQueryString(query);
    this._http.get(`${apiURL(EnvConfig.API_BILLING_DOCUMENTS)}/generateBulkExcel${queryParams}`, { responseType: "arraybuffer" }).subscribe(file => {
      this._utilsService.blobToFile(file, "application/xlsx", 'reporte-facturas' + new Date().getTime() + '.xlsx');
    });
  }
}